<template>
  <div class="rcs-step rcs-step--visual">
    <h2 class="rcs-step__heading">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.title') }}
    </h2>
    <p class="rcs-step__lead">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.subtitle') }}
    </p>

    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.brand.title') }}
      </h3>
      <div class="rcs-form-grid">
        <div class="rcs-form-grid__field">
          <label for="rcs-brand-color">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.brandColor') }}
          </label>
          <p class="rcs-form-grid__description">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.descriptions.brandColor') }}
          </p>
          <SimpleColorPicker
            id="rcs-brand-color"
            :selected-color="form.visual.brand_color"
            @color-changed="onBrandColorChanged"
          />
        </div>
        <div class="rcs-form-grid__field rcs-form-grid__field--full">
          <label for="rcs-tagline">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.tagline') }}
          </label>
          <p class="rcs-form-grid__description">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.descriptions.tagline') }}
          </p>
          <InputText
            id="rcs-tagline"
            v-model="form.visual.tagline"
            :maxlength="taglineMaxLength"
          />
        </div>
      </div>
    </section>

    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.images.title') }}
      </h3>
      <p class="rcs-carrier-section__lead">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.images.lead') }}
      </p>

      <RcsVisualImageField
        v-model="form.visual.logo_image"
        preset="logo"
        field-id="rcs-logo-image"
      />

      <RcsVisualImageField
        v-model="form.visual.banner_image"
        preset="banner"
        field-id="rcs-banner-image"
      />
    </section>

    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.contact.title') }}
      </h3>
      <div class="rcs-form-grid">
        <div class="rcs-form-grid__field">
          <label for="rcs-visual-email">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.emailAddress') }}
          </label>
          <InputText
            id="rcs-visual-email"
            v-model="form.visual.email.address"
            type="email"
            autocomplete="email"
          />
        </div>
        <div class="rcs-form-grid__field">
          <label for="rcs-visual-email-label">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.emailLabel') }}
          </label>
          <InputText
            id="rcs-visual-email-label"
            v-model="form.visual.email.label"
            :maxlength="labelMaxLength"
          />
        </div>
        <div class="rcs-form-grid__field">
          <label for="rcs-visual-phone">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.phoneNumber') }}
          </label>
          <p class="rcs-form-grid__description">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.descriptions.phoneNumber') }}
          </p>
          <InputText
            id="rcs-visual-phone"
            v-model="form.visual.phone.number"
            inputmode="numeric"
            :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.visual.placeholders.phoneNumber')"
            @blur="normalizePhoneInput"
          />
        </div>
        <div class="rcs-form-grid__field">
          <label for="rcs-visual-phone-label">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.phoneLabel') }}
          </label>
          <InputText
            id="rcs-visual-phone-label"
            v-model="form.visual.phone.label"
            :maxlength="labelMaxLength"
          />
        </div>
      </div>
    </section>

    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.website.title') }}
      </h3>
      <p class="rcs-carrier-section__lead">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.website.lead') }}
      </p>
      <div
        v-for="(entry, websiteIndex) in form.visual.website"
        :key="`website-${websiteIndex}`"
        class="rcs-carrier-repeater__card"
      >
        <div class="rcs-carrier-repeater__card-header">
          <p class="rcs-carrier-repeater__card-title">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.labels.website', { n: websiteIndex + 1 }) }}
          </p>
          <button
            v-if="form.visual.website.length > 1"
            type="button"
            class="rcs-carrier-repeater__remove"
            @click="removeWebsite(websiteIndex)"
          >
            <i
              class="fa-solid fa-trash-can"
              aria-hidden="true"
            />
            {{ t('storeParameters.services.rcsServiceParameter.wizard.carrier.actions.remove') }}
          </button>
        </div>
        <div class="rcs-form-grid">
          <div class="rcs-form-grid__field">
            <label :for="`rcs-website-address-${websiteIndex}`">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.websiteAddress') }}
            </label>
            <InputText
              :id="`rcs-website-address-${websiteIndex}`"
              v-model="entry.address"
              type="url"
              :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.visual.placeholders.websiteAddress')"
            />
          </div>
          <div class="rcs-form-grid__field">
            <label :for="`rcs-website-label-${websiteIndex}`">
              {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.websiteLabel') }}
            </label>
            <InputText
              :id="`rcs-website-label-${websiteIndex}`"
              v-model="entry.label"
              :maxlength="labelMaxLength"
            />
          </div>
        </div>
      </div>
      <div class="rcs-carrier-repeater__actions">
        <button
          type="button"
          class="rcs-carrier-repeater__add"
          @click="addWebsite"
        >
          <i
            class="fa-solid fa-plus"
            aria-hidden="true"
          />
          {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.actions.addWebsite') }}
        </button>
      </div>
    </section>

    <section class="rcs-carrier-section">
      <h3 class="rcs-carrier-section__title">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.legal.title') }}
      </h3>
      <p class="rcs-carrier-section__lead">
        {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.sections.legal.lead') }}
      </p>
      <div class="rcs-form-grid">
        <div class="rcs-form-grid__field rcs-form-grid__field--full">
          <label for="rcs-privacy-policy">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.privacyPolicy') }}
          </label>
          <InputText
            id="rcs-privacy-policy"
            v-model="form.visual.privacy_policy"
            type="url"
            :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.visual.placeholders.privacyPolicy')"
          />
        </div>
        <div class="rcs-form-grid__field rcs-form-grid__field--full">
          <label for="rcs-terms-of-service">
            {{ t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.termsOfService') }}
          </label>
          <InputText
            id="rcs-terms-of-service"
            v-model="form.visual.terms_of_service"
            type="url"
            :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.visual.placeholders.termsOfService')"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
/* eslint-disable vue/no-mutating-props, no-param-reassign */
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import SimpleColorPicker from '@/components/fields/SimpleColorPicker.vue';
import RcsVisualImageField from '@/components/rcs-onboarding/RcsVisualImageField.vue';
import {
  RCS_VISUAL_LABEL_MAX_LENGTH,
  RCS_VISUAL_TAGLINE_MAX_LENGTH,
  normalizeRcsPhoneNumber,
} from '@/types/rcs-onboarding';
import type { RcsDedicatedFormState } from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'VisualDesignStep',

  components: {
    InputText,
    SimpleColorPicker,
    RcsVisualImageField,
  },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const onBrandColorChanged = (color: string) => {
      props.form.visual.brand_color = color;
    };

    const normalizePhoneInput = () => {
      props.form.visual.phone.number = normalizeRcsPhoneNumber(
        props.form.visual.phone.number,
      );
    };

    const addWebsite = () => {
      props.form.visual.website.push({ address: '', label: '' });
    };

    const removeWebsite = (index: number) => {
      props.form.visual.website.splice(index, 1);
    };

    return {
      t,
      taglineMaxLength: RCS_VISUAL_TAGLINE_MAX_LENGTH,
      labelMaxLength: RCS_VISUAL_LABEL_MAX_LENGTH,
      onBrandColorChanged,
      normalizePhoneInput,
      addWebsite,
      removeWebsite,
    };
  },
});
</script>
